/* Shared building blocks for the Webinars page (pages/WebinarsPage.jsx) and
 * the slimmed Administration -> Webinar tab (components/WebinarAdmin.jsx).
 *
 * Nothing here decides access: every write is gated server-side
 * (webinar.config, or webinar.attendance.correct for attendance
 * corrections). useWebinarCaps() only hides controls the user cannot use.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge } from "@/components/common";

export const useWebinarCaps = () => {
  const { hasCap } = useAuth();
  const canConfig = hasCap("webinar.config");
  return {
    canView: canConfig || hasCap("webinar.view"),
    canConfig,
    canCorrectAttendance: hasCap("webinar.attendance.correct"),
  };
};

const TONE = {
  success: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  warning: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  danger: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  muted: "bg-muted text-muted-foreground border",
};
export const toneClass = (tone) => TONE[tone] || "";

const STATUS_TONE = { draft: "muted", active: "success", paused: "warning", completed: "muted", cancelled: "danger" };

export const WebinarStatusBadge = ({ status }) => (
  <Badge variant="outline" className={`text-[10px] capitalize ${toneClass(STATUS_TONE[status])}`}>
    {status || "unknown"}
  </Badge>
);

const REG_TONE = {
  registered: "success",
  registration_link_sent: "warning",
  external_registration_pending: "warning",
  registration_pending: "warning",
  pending: "muted",
  registration_failed: "danger",
  cancelled: "muted",
};

export const RegStateBadge = ({ state, testId }) => (
  <Badge variant="outline" data-testid={testId}
         className={`text-[10px] ${toneClass(REG_TONE[state])}`}>
    {(state || "unknown").replace(/_/g, " ")}
  </Badge>
);

export const SEGMENT_ORDER = ["no_show", "attended_under_50", "attended_50_74", "attended_75_plus"];

export const METHOD_LABELS = {
  api: "Provider API registration",
  external_link: "Shared registration link (WhatsApp)",
};

export const REGISTRATION_MODE_LABELS = {
  auto: "Register automatically on enrollment",
  whatsapp_optin: "Ask on WhatsApp first (opt-in)",
};

/* Provider capability matrix (A1). The rows come from the backend's
 * PROVIDER_CAPABILITIES merged with live configuration state — this file
 * never hardcodes what a provider can do, so the UI can't overstate it. */
const CAPABILITY_LABELS = {
  api_registration: "Registers attendees via API",
  external_link_registration: "Sends provider registration page (WhatsApp)",
  unique_attendee_link: "Unique join link per attendee",
  attendance_webhook: "Signed attendance webhook",
  attendance_api: "Attendance pulled from provider",
  attendance_duration: "Attendance duration / %",
  cancellation: "Cancels registration provider-side",
  retry: "Safe retry of failed registration",
};

const VERIFICATION_LABELS = {
  simulated: "Simulated — never contacts a real provider",
  blocked_missing_credentials: "Blocked — credentials missing",
  blocked_disabled: "Blocked — turned off in Integrations",
  configured_not_live_verified: "Configured — not yet live-verified",
};

// Fallback used only while settings are still loading.
export const FALLBACK_PROVIDERS = [
  { provider: "fake", label: "Simulated (no real provider)", registration_methods: ["api"], default_registration_method: "api" },
  { provider: "zoom", label: "Zoom Webinars", registration_methods: ["api"], default_registration_method: "api" },
  { provider: "webinarjam", label: "WebinarJam", registration_methods: ["external_link", "api"], default_registration_method: "external_link" },
  { provider: "everwebinar", label: "EverWebinar", registration_methods: ["external_link", "api"], default_registration_method: "external_link" },
];

export const ProviderCapabilityPanel = ({ row, method, testId = "webinar-provider-capabilities" }) => {
  if (!row) return null;
  const caps = (row.method_capabilities && row.method_capabilities[method]) || row.capabilities || {};
  const usable = (row.usable_registration_methods || []).includes(method);
  return (
    <div className="rounded-md border p-2 space-y-1.5 text-[11px]" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-xs">{row.label}</span>
        {method === "external_link" && usable && (
          <Badge variant="outline" className="text-[10px]" data-testid={`${testId}-link-status`}>
            Shared link — no credentials needed
          </Badge>
        )}
        {row.live_verification_status && (
          <Badge variant="outline" className="text-[10px]" data-testid={`${testId}-status`}>
            {/* live_verification_status describes the provider's API
                credentials, so it is labelled as such next to a shared-link
                method rather than read as "this method is blocked". */}
            {row.is_real && (row.registration_methods || []).includes("api") && method !== "api" ? "API: " : ""}
            {VERIFICATION_LABELS[row.live_verification_status] || row.live_verification_status}
          </Badge>
        )}
      </div>
      {row.summary && <p className="text-muted-foreground">{row.summary}</p>}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
        {Object.keys(CAPABILITY_LABELS).map((k) => (
          <li key={k} className="flex items-center gap-1" data-testid={`${testId}-${k}`}>
            <span className={caps[k] ? "text-[hsl(var(--success))]" : "text-muted-foreground"}>
              {caps[k] ? "✓" : "✗"}
            </span>
            <span className={caps[k] ? "" : "text-muted-foreground line-through"}>{CAPABILITY_LABELS[k]}</span>
          </li>
        ))}
      </ul>
      {row.is_real && method === "api" && !usable && (
        <p className="text-[hsl(var(--danger))]" data-testid={`${testId}-blocked`}>
          API registration is blocked for a live campaign:{" "}
          {(row.missing_credentials || []).length
            ? `missing ${row.missing_credentials.join(", ")}`
            : "provider is turned off in Integrations"}. TEST MODE campaigns still use the simulated provider.
        </p>
      )}
      {(row.notes || []).length > 0 && (
        <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
          {row.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
    </div>
  );
};

/* A small reason/evidence prompt used by confirm/cancel/import actions. */
export const TextPromptDialog = ({ open, onOpenChange, title, label, multiline, placeholder, onSubmit, testId }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setValue(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <Label className="text-xs">{label}</Label>
        {multiline
          ? <Textarea rows={6} value={value} placeholder={placeholder} data-testid={`${testId}-input`}
                      onChange={(e) => setValue(e.target.value)} />
          : <Input value={value} placeholder={placeholder} data-testid={`${testId}-input`}
                   onChange={(e) => setValue(e.target.value)} />}
        <DialogFooter>
          <Button disabled={busy || !value.trim()} data-testid={`${testId}-submit`}
                  onClick={async () => {
                    setBusy(true);
                    // onSubmit already toasts its own error; the dialog just stays open.
                    try { await onSubmit(value.trim()); onOpenChange(false); } catch (_) { /* shown via toast */ } finally { setBusy(false); }
                  }}>Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Parses a pasted provider registrant export: one row per line, either a
 * bare email/phone or "email,join_url". Header rows are skipped. */
export const parseRegistrantRows = (text) => (text || "")
  .split(/\r?\n/)
  .map((line) => line.split(",").map((c) => c.trim()))
  .filter((cols) => cols[0] && !/^e-?mail$/i.test(cols[0]))
  .map((cols) => {
    const first = cols[0];
    const row = first.includes("@") ? { email: first } : { phone: first };
    const url = cols.find((c) => c.startsWith("https://"));
    if (url) row.join_url = url;
    return row;
  });

export const StatTile = ({ label, value, hint, tone, testId }) => (
  <div className={`rounded-md border p-2.5 ${tone ? toneClass(tone) : ""}`} data-testid={testId}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="text-xl font-semibold tabular-nums">{value ?? "—"}</p>
    {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

/* Campaign picker shared by the per-campaign tabs. `allowAll` adds an
 * "All campaigns" option (value ""). */
export const CampaignPicker = ({ campaigns, value, onChange, allowAll = false, testId = "webinar-campaign-picker" }) => {
  const items = campaigns || [];
  return (
    <Select value={value || (allowAll ? "__all__" : undefined)}
            onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger className="h-9 w-full sm:w-[360px]" data-testid={testId}>
        <SelectValue placeholder={items.length ? "Choose a campaign" : "No campaigns yet"} />
      </SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="__all__">All campaigns</SelectItem>}
        {items.map((c) => (
          <SelectItem key={c.campaign_id} value={c.campaign_id}>
            {c.name}{c.test_mode ? " · TEST" : ""} · {c.status}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const CampaignHeadline = ({ campaign }) => (
  <span className="inline-flex items-center gap-1.5 flex-wrap">
    <span className="font-medium">{campaign?.name}</span>
    <WebinarStatusBadge status={campaign?.status} />
    <TestBadge show={campaign?.test_mode} />
    {campaign && !campaign.test_mode && (
      <Badge variant="outline" className={`text-[10px] ${toneClass("danger")}`}>LIVE</Badge>
    )}
  </span>
);

/* ------------------------------------------------------------------ timezone
 * Every campaign carries ONE explicit IANA timezone (2026-09 round 7). A
 * session time is never shown without its zone, and the wall-clock start a
 * user types is always read in the CAMPAIGN's zone — never the browser's.
 */
export const DEFAULT_TIMEZONE = "Asia/Kolkata";

export const COMMON_TIMEZONES = [
  "Asia/Kolkata", "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
  "Australia/Perth", "Australia/Adelaide", "Pacific/Auckland", "Asia/Dubai",
  "Asia/Singapore", "Asia/Tokyo", "Europe/London", "America/New_York",
  "America/Los_Angeles", "UTC",
];

/* '20 Nov 2026, 10:00 am AEDT' — formatted in `tz`, zone abbreviation shown. */
export const fmtInTz = (iso, tz, { year = true } = {}) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: tz || DEFAULT_TIMEZONE, day: "2-digit", month: "short",
      ...(year ? { year: "numeric" } : {}),
      hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short",
    }).format(new Date(iso));
  } catch (_) { return String(iso); }
};

/* UTC instant -> the 'YYYY-MM-DDTHH:mm' wall-clock string a datetime-local
 * input wants, expressed in `tz` (not the browser's zone). */
export const utcToLocalInput = (iso, tz) => {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || DEFAULT_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(iso)).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch (_) { return ""; }
};

/* ------------------------------------------------------ readiness status UI */
const READINESS_TONE = { ok: "success", missing: "danger", warning: "warning", info: "muted" };
const READINESS_LABEL = { ok: "OK", missing: "Missing", warning: "Attention", info: "Info" };

export const ReadinessBadge = ({ status, testId }) => (
  <Badge variant="outline" data-testid={testId}
         className={`text-[10px] ${toneClass(READINESS_TONE[status] || "muted")}`}>
    {READINESS_LABEL[status] || status}
  </Badge>
);
